/**
 * Utility functions for Step Form operations
 */

import { formatVersionHistoryIdDisplay } from "../common/VersionHistoryPopup/formatVersionHistoryIdDisplay";

/**
 * Generate step preview HTML from step details
 * @param {Array} stepDetails - Array of step detail objects
 * @returns {string} - Preview HTML with preserved formatting
 */
export const generateStepPreview = (stepDetails) => {
  return stepDetails
    .map((step) => {
      const partText = step.partNumber ? ` (${step.partNumber})` : "";
      // Only show description if there's a part number - description is associated with the part
      const descriptionText = step.partNumber
        ? step.description || step.part_info?.description || ""
        : "";

      const qtyText = step.qty && step.qty != 0 ? ` Qty ${step.qty}` : "";
      // Preserve HTML formatting in description (font colors, etc.)
      return `${step.action}${partText} ${descriptionText}${qtyText}`;
    })
    .join(" ");
};

/**
 * Sort version history data
 * @param {Array} data - Version history data
 * @param {Object} sortConfig - Sort configuration {key, direction}
 * @returns {Array} - Sorted data
 */
export const getSortedVersionHistory = (data, sortConfig) => {
  if (!data || !Array.isArray(data)) return [];

  const sortedData = [...data];
  const { key, direction } = sortConfig;

  if (key) {
    sortedData.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === "date") {
        aValue = new Date(a.audit_date);
        bValue = new Date(b.audit_date);
      } else if (key === "versionId") {
        aValue = a.version_id;
        bValue = b.version_id;
      } else if (key === "status") {
        aValue = a.action || "";
        bValue = b.action || "";
      } else if (key === "modifiedBy") {
        aValue = `${a.first_name || ""} ${a.last_name || ""}`;
        bValue = `${b.first_name || ""} ${b.last_name || ""}`;
      } else if (key === "comment") {
        aValue = a.comment || "";
        bValue = b.comment || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  }

  return sortedData;
};

/**
 * Format date for version history display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatVersionHistoryDate = (dateString) => {
  // Get app setup state from Redux store
  let appSetupState = null;
  if (typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetupState = state.appSetup;
  }
  
  const { formatDateToAppTimezone } = require("./timezoneUtils");
  return formatDateToAppTimezone(dateString, appSetupState);
};

/**
 * Generate "Last Saved" text
 * @param {string} timestamp - ISO timestamp
 * @param {string} userName - User name
 * @returns {string} - Formatted last saved text
 */
export const generateLastSavedText = (timestamp, userName) => {
  if (!timestamp) return "";

  const rawUserName = userName || "Unknown User";
  const formattedUserName = rawUserName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // Get app setup state from Redux store
  let appSetupState = null;
  if (typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetupState = state.appSetup;
  }
  
  const { formatDateToAppTimezoneAlt, getAppTimezoneName } = require("./timezoneUtils");
  const timezone = getAppTimezoneName(appSetupState);
  const date = new Date(timestamp);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  
  const hourPart = parts.find((p) => p.type === "hour")?.value || "";
  const minutePart = parts.find((p) => p.type === "minute")?.value || "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";
  let hours = parseInt(hourPart, 10);
  const minutes = minutePart.padStart(2, "0");
  const ampm = dayPeriod.toUpperCase() || (hours >= 12 ? "PM" : "AM");
  hours = hours % 12 || 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  // Get timezone abbreviation
  const { getAppTimezoneAbbr } = require("./timezoneUtils");
  const tzAbbr = getAppTimezoneAbbr(appSetupState);

  return `Last Saved On ${formattedDate} at ${formattedTime} ${tzAbbr} By ${formattedUserName}`;
};

/**
 * Export version history to CSV
 * @param {Array} versionHistories - Version history data
 * @returns {void} - Downloads CSV file
 */
export const exportVersionHistoryToCSV = (
  versionHistories,
  breadcrumbTextProp = "",
) => {
  const header = ["Date", "Version ID", "Activity", "Modified by", "Comment"];
  const rows = versionHistories.map((row) => {
    const finalString = formatVersionHistoryDate(row.audit_date);
    const versionIdWithPath = formatVersionHistoryIdDisplay(
      row,
      breadcrumbTextProp,
    );

    return [
      finalString,
      versionIdWithPath,
      row.action || "-",
      `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
      row.comment || "-",
    ];
  });

  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${v}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "version_history.csv";
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Clean blank rows from table data
 * @param {Array} tableData - Table data array
 * @param {number} excludeIndex - Index to exclude from cleaning
 * @param {Array} requiredFields - Fields that must be filled
 * @returns {Array} - Cleaned table data
 */
export const cleanBlankRows = (tableData, excludeIndex, requiredFields) => {
  return tableData.filter((row, i) => {
    if (i === excludeIndex) return true;

    const isBlank = requiredFields.every((field) => !row[field]);
    return !(row.isNew && isBlank);
  });
};

/**
 * Check if a row is blank based on required fields
 * @param {Object} row - Row data
 * @param {Array} requiredFields - Fields that must be filled
 * @returns {boolean} - True if row is blank
 */
export const isBlankRow = (row, requiredFields) => {
  return requiredFields.every((field) => !row[field]);
};

/**
 * Transform parts required data for preview
 * @param {Array} bomTable - BOM table data
 * @returns {Array} - Transformed data
 */
export const transformPartsRequired = (bomTable) => {
  return bomTable.map((item) => ({
    part_info: {
      type_info: {
        name: item.type || "",
      },
      part_number: item.partNumber || "",
      description: item.description || "",
      unit_info: {
        unit_name: item.unit || "",
      },
    },
    qty: item.qty || 0,
  }));
};

/**
 * Transform step details data for preview
 * @param {Array} stepDetails - Step details data
 * @returns {Array} - Transformed data
 */
export const transformStepDetails = (stepDetails) => {
  return stepDetails.map((step) => ({
    action: step.action,
    qty: step.qty || 0,
    part_ref: step.partId,
    unit_ref: step.unitId,
    description: step.description,
    part_info: {
      part_id: step.partId,
      part_number: step.partNumber,
      description: step.description,
      type_info: {
        name: step.type || "",
      },
      unit_info: {
        unit_id: step.unit,
        unit_type_id_ref: step.unit_type_id_ref,
      },
    },
  }));
};

/**
 * Generate breadcrumb text for step
 * @param {string} partNumber - Part number
 * @param {string} revision - Revision
 * @param {string} section - Section name
 * @param {string} stepTitle - Step title
 * @param {Object} locationState - Location state object
 * @returns {string} - Breadcrumb text
 */
export const generateStepBreadcrumb = (
  partNumber,
  revision,
  section,
  stepTitle,
  locationState
) => {
  const sectionText =
    section === "" || section === "undefined" || section === "null"
      ? `Section ${parseInt(locationState?.sourceSectionIndex || 0) + 1}`
      : section;
  return `${partNumber} > Rev ${revision} > ${sectionText} > ${stepTitle}`;
};
